// The org's cost visibility, the money policy's one input from the server (UI-MONEY-1, REQ-233):
// read from the org's settings for `useOrgContext`. Fail closed (U4): null — no money on the
// customer surface — until the read answers, after the org changes, when the read fails, and when
// the caller does not read it at all. An answer for an org the screen has left is never shown.
import { useEffect, useState } from "preact/hooks";
import type { ApiClient } from "./client";
import type { CostVisibility } from "./org-context";

interface Answer {
  orgId: string;
  costVisibility: CostVisibility;
}

/**
 * `orgId`'s cost visibility, or null until known. `enabled` false (or no org) reads nothing; the
 * caller decides who needs it (a billing principal, the customer surface).
 */
export function useCostVisibility(api: ApiClient, orgId: string | undefined, enabled = true): CostVisibility | null {
  const [answer, setAnswer] = useState<Answer | null>(null);
  const reads = enabled && orgId !== undefined;

  useEffect(() => {
    setAnswer(null);
    if (!enabled || orgId === undefined) return;
    let current = true;
    api
      .get("/v1/orgs/{orgId}/settings", { path: { orgId } })
      .then((settings) => current && setAnswer({ orgId, costVisibility: settings.costVisibility }))
      .catch(() => current && setAnswer(null));
    return () => {
      current = false;
    };
  }, [api, orgId, enabled]);

  // Keyed by org, so the render between an org change and the effect never shows the old org's.
  return reads && answer?.orgId === orgId ? answer.costVisibility : null;
}
